import { Dialog, Transition } from '@headlessui/react';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { ExchangeIcon, FacebookIcon } from 'components/icons';
import {
  AdsAccountSelector,
  BusinessAccountsSelector,
  getAdAccounts,
  getBusinessAccounts,
  useFacebookSdk,
} from 'components/integrations/facebook';
import { ButtonCTASmall, LoadingSpinner, SpinnerSmall } from 'components/ui';
import { forEach } from 'lodash';
import querystring from 'querystring';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import {
  useAddIntegrationMutation,
  useGetStoreInfoQuery,
} from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { LocationState } from '../types';
import type {
  AdAccountsSelection,
  BusinessAccount,
  FacebookAdAccountsResponse,
  FacebookBusinessAccounts,
  FacebookLogin,
} from './types';

export const FacebookConnect = () => {
  const { query } = useParseURLQueryParams();
  const { isFacebookSdkLoaded } = useFacebookSdk();
  const [facebookLoggedIn, setFacebookLoggedIn] = useState(false);
  const [privateKey, setPrivateKey] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [businessAccounts, setBusinessAccounts] = useState<
    FacebookBusinessAccounts['data']
  >([]);

  const [adAccounts, setAdAccounts] = useState<AdAccountsSelection>({});

  const [selectedBusinessAccount, setSelectedBusinessAccount] =
    useState<BusinessAccount>({
      id: 'select_account',
      name: 'Select account',
    });

  const buttonRef = useRef(null);
  const history = useHistory();

  const location = useLocation<LocationState>();

  const handleCloseModal = () => {
    if (location?.state?.background) {
      history.push(location.state.background);
    } else {
      history.replace({
        ...history.location,
        pathname: '/integrations/connect',
      });
    }
  };

  const queryParams = querystring.stringify({
    store_name: query.store_name,
  });

  const { data: storeInfo } = useGetStoreInfoQuery(queryParams, {
    skip: !!!query.store_name,
  });

  const [addIntegration, { isLoading }] = useAddIntegrationMutation();

  // Get Business Accounts
  useEffect(() => {
    if (privateKey && publicKey) {
      getBusinessAccounts({
        userId: publicKey,
        accessToken: privateKey,
      })
        .then((response: FacebookBusinessAccounts) => {
          setBusinessAccounts(response.data);
        })
        .catch((error) => toast.error('Failed to perform the task'));
    }
  }, [privateKey, publicKey]);

  // Get Ads Accounts
  useEffect(() => {
    if (businessAccounts?.length && privateKey) {
      businessAccounts.forEach((account) => {
        getAdAccounts({
          businessId: account.id,
          accessToken: privateKey,
        })
          .then((response: FacebookAdAccountsResponse) => {
            setAdAccounts((prev) => ({
              ...prev,
              [account.id]: response.data.map((item) => ({
                ...item,
                selected: false,
              })),
            }));
          })
          .catch((error) => toast.error('Failed to perform the task'));
      });
    }
  }, [businessAccounts, privateKey]);

  const handleContinueWithFacebook = () => {
    (window as any).FB.getLoginStatus((response: FacebookLogin) => {
      if (response.status === 'connected') {
        toast.success('Facebook authorized successfully');
        setPublicKey(response.authResponse.userID);
        setPrivateKey(response.authResponse.accessToken);
        setFacebookLoggedIn(true);
      } else {
        (window as any).FB.login(
          (response: FacebookLogin) => {
            if (response.authResponse) {
              toast.success('Facebook authorized successfully');
              setPublicKey(response.authResponse.userID);
              setPrivateKey(response.authResponse.accessToken);
              setFacebookLoggedIn(true);
            } else {
              console.log('User cancelled login or did not fully authorize.');
            }
          },
          { scope: process.env.REACT_APP_FACEBOOK_TOKEN_SCOPE }
        );
      }
    });
  };

  const handleSubmit = () => {
    const adAccountIds: string[] = [];
    const businessAccountIds: string[] = [];

    forEach(adAccounts, (value, key) => {
      const selectedAdAccount = value.filter((item) => item.selected);
      if (selectedAdAccount.length) {
        selectedAdAccount.forEach((selected) => {
          adAccountIds.push(selected.id);
        });
        businessAccountIds.push(key);
      }
    });
    const payload = {
      facebook_user_id: publicKey,
      access_token: privateKey,
      ad_account_ids: adAccountIds.join(','),
      business_account_ids: businessAccountIds.join(','),
    };
    addIntegration({
      storeId: Number(storeInfo?.id),
      provider: 'facebook',
      body: payload,
    })
      .unwrap()
      .then(handleCloseModal);
  };

  return (
    <Transition show as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        static
        open
        initialFocus={buttonRef}
        onClose={handleCloseModal}
      >
        <div className="min-h-screen text-center md:px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100 "
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full my-8 overflow-auto text-left align-middle transition-all transform bg-white shadow-xl lg:overflow-hidden rounded-2xl h-segments-516px md:w-segments-648px">
              <div className="w-full h-48 px-5 pt-9 lg:px-12 bg-segments-accent-color-1 bg-opacity-5">
                <p className="text-black text-opacity-50 text-segments-13px">
                  Integrations
                </p>
                <div
                  className="flex items-center mt-8 space-x-2 focus:outline-none"
                  ref={buttonRef}
                >
                  <FacebookIcon />
                  <ExchangeIcon />
                  <p>{storeInfo?.name}</p>
                </div>
                <p className="mt-4 text-2xl">Connect to Facebook</p>
              </div>
              <div className="flex flex-col justify-between px-5 pt-8 text-gray-700 lg:px-12 h-80">
                {!!!isFacebookSdkLoaded && (
                  <div className="flex justify-center mt-10" tabIndex={0}>
                    <LoadingSpinner className="w-16 h-16 text-segments-accent-color-orange" />
                  </div>
                )}
                {isFacebookSdkLoaded && !!!facebookLoggedIn && (
                  <div className="mx-auto text-center">
                    <p className="text-black text-opacity-50" tabIndex={0}>
                      Log in to your account:
                    </p>
                    <button
                      className="mt-8"
                      ref={buttonRef}
                      onClick={handleContinueWithFacebook}
                    >
                      <img
                        src="/assets/logo-facebook-button.png"
                        alt="facebook button"
                        width="250"
                      />
                    </button>
                    <div className="mt-6 text-segments-13px p-3 bg-[#FFECD6] rounded-xl w-full">
                      <div className="space-y-3 ">
                        <div className="flex space-x-3">
                          <ExclamationCircleIcon className="w-5 h-5" />

                          <p className="text-black text-opacity-60">
                            Please make sure you have accepted{' '}
                            <a
                              href="https://business.facebook.com/ads/manage/customaudiences/tos"
                              className="font-bold text-segments-link"
                            >
                              Facebook Terms of Service
                            </a>{' '}
                            before proceeding with the integration.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {facebookLoggedIn && (
                  <div className="md:grid md:grid-cols-6">
                    <div className="col-span-2">
                      <p className="text-black text-opacity-50">
                        Business Accounts:
                      </p>
                      <BusinessAccountsSelector
                        businessAccounts={businessAccounts}
                        selectedBusinessAccount={selectedBusinessAccount}
                        setSelectedBusinessAccount={setSelectedBusinessAccount}
                      />
                    </div>
                    <div className="col-span-4 mt-4 md:mt-0">
                      <div>
                        <AdsAccountSelector
                          adAccounts={adAccounts}
                          setAdAccounts={setAdAccounts}
                          selectedBusinessAccount={selectedBusinessAccount}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex justify-between mt-4 mb-6 ">
                  <div>
                    <ButtonCTASmall
                      type="button"
                      variant="secondary"
                      onClick={handleCloseModal}
                    >
                      <p className="px-4 font-normal text-segments-13px">
                        Cancel
                      </p>
                    </ButtonCTASmall>
                  </div>
                  <div className="">
                    <ButtonCTASmall
                      variant="main"
                      type="button"
                      onClick={handleSubmit}
                      disabled={isLoading}
                    >
                      <p className="w-20 text-segments-13px">
                        {isLoading ? <SpinnerSmall /> : 'Submit'}
                      </p>
                    </ButtonCTASmall>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
